import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { createWorker } from 'tesseract.js';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const renderPdfPagesToImages = async (
  file: File,
  mode: 'printed' | 'handwriting' = 'printed'
): Promise<Blob[]> => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const blobs: Blob[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const baseScale = mode === 'handwriting' ? 1.5 : 2;
    let viewport = page.getViewport({ scale: baseScale });
    const MAX_DIM = mode === 'handwriting' ? 1600 : 4000;
    const longest = Math.max(viewport.width, viewport.height);
    const scale = longest > MAX_DIM ? (baseScale * MAX_DIM) / longest : baseScale;
    viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d')!;
    await page.render({ canvasContext: ctx, viewport, canvas } as any).promise;
    const blob: Blob = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b!), 'image/png')
    );
    blobs.push(blob);
  }
  return blobs;
};

const downscaleImageForHandwriting = async (file: File): Promise<Blob> => {
  const MAX_DIM = 1600;
  const dataUrl = await blobToDataUrl(file);
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  const longest = Math.max(img.width, img.height);
  const scale = longest > MAX_DIM ? MAX_DIM / longest : 1;
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  return await new Promise<Blob>((resolve) =>
    canvas.toBlob((b) => resolve(b!), 'image/png')
  );
};

const recognizeHandwriting = async (
  source: Blob | File,
  language: string
): Promise<string> => {
  const dataUrl = await blobToDataUrl(source);
  const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ocr-handwriting`;
  const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: publishableKey,
      Authorization: `Bearer ${publishableKey}`,
    },
    body: JSON.stringify({ imageDataUrl: dataUrl, language }),
  });

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.error || `Handwriting OCR failed with status ${response.status}`);
  }
  if (data?.error) throw new Error(data.error);
  return (data?.text ?? '').trim();
};

interface OCRButtonProps {
  onTextExtracted: (text: string) => void;
}

const OCRButton: React.FC<OCRButtonProps> = ({ onTextExtracted }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentProcessing, setCurrentProcessing] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('eng');
  const [handwritingMode, setHandwritingMode] = useState(false);
  const { toast } = useToast();

  const supportedFormats = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp',
    'image/tiff', 'image/webp', 'application/pdf'
  ];

  const languages = [
    { code: 'eng', name: 'English' },
    { code: 'spa', name: 'Spanish' },
    { code: 'fra', name: 'French' },
    { code: 'deu', name: 'German' },
    { code: 'ita', name: 'Italian' },
    { code: 'por', name: 'Portuguese' },
    { code: 'rus', name: 'Russian' },
    { code: 'chi_sim', name: 'Chinese Simplified' },
    { code: 'chi_tra', name: 'Chinese Traditional' },
    { code: 'jpn', name: 'Japanese' },
    { code: 'kor', name: 'Korean' },
    { code: 'ara', name: 'Arabic' },
    { code: 'hin', name: 'Hindi' },
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    const validFiles = selectedFiles.filter(file => supportedFormats.includes(file.type));

    if (validFiles.length !== selectedFiles.length) {
      const invalidFiles = selectedFiles.filter(file => !supportedFormats.includes(file.type));
      toast({
        title: "Unsupported files detected",
        description: `${invalidFiles.length} file(s) were skipped. Supported formats: PDF, JPEG, PNG, GIF, BMP, TIFF, WebP.`,
        variant: "destructive",
      });
    }

    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const processFiles = async () => {
    if (files.length === 0) {
      toast({
        title: "No files selected",
        description: "Please select at least one file to process.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    let allExtractedText = '';

    try {
      const worker = handwritingMode ? null : await createWorker(selectedLanguage);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const modeLabel = handwritingMode ? 'Transcribing handwriting' : 'Processing';
        setCurrentProcessing(`${modeLabel} ${file.name}... (${i + 1}/${files.length})`);

        try {
          if (!supportedFormats.includes(file.type)) {
            toast({
              title: `Skipped ${file.name}`,
              description: "Unsupported file type.",
              variant: "destructive",
            });
            continue;
          }

          let fileText = '';
          if (file.type === 'application/pdf') {
            const pageImages = await renderPdfPagesToImages(
              file,
              handwritingMode ? 'handwriting' : 'printed'
            );
            for (let p = 0; p < pageImages.length; p++) {
              setCurrentProcessing(`${modeLabel} ${file.name} - page ${p + 1}/${pageImages.length} (${i + 1}/${files.length})`);
              let text = '';
              try {
                if (handwritingMode) {
                  text = await recognizeHandwriting(pageImages[p], selectedLanguage);
                } else {
                  const res = await worker!.recognize(pageImages[p]);
                  text = res.data.text;
                }
              } catch (pageErr) {
                toast({
                  title: `Page ${p + 1} of ${file.name} failed`,
                  description: pageErr instanceof Error ? pageErr.message : 'Unknown error. Continuing with next page.',
                  variant: 'destructive',
                });
                continue;
              }
              if (text.trim()) {
                fileText += `\n\n--- Page ${p + 1} ---\n${text.trim()}`;
              }
            }
          } else {
            if (handwritingMode) {
              const prepared = await downscaleImageForHandwriting(file);
              fileText = await recognizeHandwriting(prepared, selectedLanguage);
            } else {
              const { data: { text } } = await worker!.recognize(file);
              fileText = text;
            }
          }

          if (fileText.trim()) {
            allExtractedText += `\n\n--- Text from ${file.name} ---\n${fileText.trim()}`;
          } else {
            toast({
              title: `No text found in ${file.name}`,
              description: "This file may not contain readable text.",
              variant: "destructive",
            });
          }
        } catch (fileError) {
          console.error(`Error processing ${file.name}:`, fileError);
          toast({
            title: `Failed to process ${file.name}`,
            description: fileError instanceof Error ? fileError.message : "This file could not be processed. Continuing with other files.",
            variant: "destructive",
          });
        }
      }

      if (worker) await worker.terminate();

      if (allExtractedText.trim()) {
        onTextExtracted(allExtractedText.trim());
        toast({
          title: "OCR completed",
          description: `Successfully extracted text using ${languages.find(l => l.code === selectedLanguage)?.name || selectedLanguage}.`,
        });
        setShowDialog(false);
        setFiles([]);
        setCurrentProcessing('');
      } else {
        toast({
          title: "No text extracted",
          description: "No readable text was found in any of the uploaded files.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('OCR processing error:', error);
      toast({
        title: "OCR failed",
        description: "Failed to process the files. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setCurrentProcessing('');
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setShowDialog(true)}
        title="Extract text from PDFs or images (OCR)"
      >
        <FileText className="h-4 w-4" />
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Extract Text from PDFs or Images (OCR)</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-sm text-muted-foreground bg-accent/50 p-3 rounded-md">
              <p className="mb-2"><strong>Supported formats:</strong></p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>PDF documents</li>
                <li>Images: JPEG, PNG, GIF, BMP, TIFF, WebP</li>
              </ul>
              <p className="mt-2 text-xs"><strong>Limitations:</strong> Processing time depends on file size, page count, and complexity. Best results with clear, high-contrast text.</p>
            </div>

            <div className="flex items-start justify-between gap-3 rounded-md border p-3">
              <div className="space-y-1">
                <Label htmlFor="handwriting-mode" className="text-sm">Handwritten notes</Label>
                <p className="text-xs text-muted-foreground">
                  Enable for cursive, manuscript, or calligraphy. Uses AI vision (slower, higher accuracy on handwriting). Leave off for printed text.
                </p>
              </div>
              <Switch
                id="handwriting-mode"
                checked={handwritingMode}
                onCheckedChange={setHandwritingMode}
                disabled={isProcessing}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="language-select">Language</Label>
              <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                <SelectTrigger>
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="file-upload">Select Files (PDF or Images)</Label>
              <Input
                id="file-upload"
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp"
                onChange={handleFileSelect}
                disabled={isProcessing}
              />
            </div>

            {files.length > 0 && (
              <div className="space-y-2">
                <Label>Selected Files ({files.length})</Label>
                <div className="max-h-32 overflow-y-auto space-y-2">
                  {files.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="flex items-center justify-between p-2 bg-muted rounded text-xs">
                      <div className="flex-1 truncate">
                        <div className="font-medium truncate">{file.name}</div>
                        <div className="text-muted-foreground">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                      {!isProcessing && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          className="ml-2 h-6 w-6 p-0"
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={processFiles}
                disabled={isProcessing || files.length === 0}
                className="flex-1"
              >
                {isProcessing ? 'Processing...' : 'Extract Text'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDialog(false);
                  setFiles([]);
                  setCurrentProcessing('');
                }}
                disabled={isProcessing}
              >
                Cancel
              </Button>
            </div>

            {isProcessing && (
              <div className="text-sm text-primary bg-accent/50 p-3 rounded-md">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  <span>{currentProcessing || 'Processing files... This may take a few moments.'}</span>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OCRButton;
